import { Icon } from "@/components/ui/Icon";
import { unitDomId } from "@/lib/memoFocusUnits";
import { t } from "@/lib/strings";

/** The heading's step under its Abschnitt. Containment shown spatially (variant A2). */
export const SECTION_INDENT = 16;
/** Body + reveal, relative to the heading. 18 is what member points already use,
 *  so nothing shifts horizontally when this primitive lands. */
export const SECTION_BODY_INDENT = 18;

export interface RevealState {
  expanded: boolean;
  /** Points closed in THIS section this week. 0 → the label is `frühere Punkte`. */
  closedCount: number;
}

interface Props {
  /** Subheading entry id, else the block id — the presentation cursor's anchor. */
  anchorId: string;
  /** The rendered Zwischenüberschrift row, or null for a leading run. */
  heading: React.ReactNode | null;
  /** The section's entry rows. */
  children: React.ReactNode;
  /** null → this section has nothing behind it and shows NO control. */
  reveal: RevealState | null;
  onToggleReveal: () => void;
  /** Presentation: dim + make inert. */
  dim: boolean;
}

/**
 * One Abschnitt-level group, rendered with UNCONDITIONAL geometry.
 *
 * 🔴 Every section gets the same rule, indent and body offset regardless of its
 * state. The previous code applied a left border + padding ONLY to sections that
 * had a reveal, so a section with history sat 10px right of one without and two
 * same-level Zwischenüberschriften genuinely rendered at different x. Geometry
 * that depends on content is how hierarchy drifts.
 *
 * The reveal is rendered HERE, from a descriptor — never by the entry loop — so a
 * section can structurally own at most one.
 */
export function MemoSectionView({
  anchorId,
  heading,
  children,
  reveal,
  onToggleReveal,
  dim,
}: Props) {
  const headed = heading !== null;
  return (
    <div
      id={unitDomId({ anchorId })}
      style={{
        // opacity + pointer-events ALWAYS move together: a dimmed section must
        // never stay clickable, or a stray click while screen-sharing opens an editor.
        opacity: dim ? 0.35 : 1,
        pointerEvents: dim ? "none" : "auto",
        transition: "opacity 120ms ease",
        scrollMarginTop: 80,
        marginLeft: headed ? SECTION_INDENT : 0,
        // The rule + the accent bar are what announce a new section. They stay at
        // full strength even when the heading is closed — only its TEXT greys out
        // (MemoSubheadingRow), or a retired heading folds into the section above.
        borderTop: headed ? "1px solid var(--hairline)" : undefined,
        paddingTop: headed ? 10 : undefined,
        marginTop: headed ? 16 : 0,
      }}
    >
      {heading}
      <div style={{ marginLeft: SECTION_BODY_INDENT }}>{children}</div>
      {reveal ? (
        <button
          type="button"
          className="flow-memo-reveal"
          onClick={onToggleReveal}
          style={{
            marginTop: 4,
            marginLeft: SECTION_BODY_INDENT,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontSize: 11.5,
            fontWeight: 700,
            color: "var(--ink-3)",
            background: "none",
            border: "none",
            cursor: "pointer",
          }}
        >
          <Icon name={reveal.expanded ? "chevDown" : "chevRight"} size={13} color="var(--ink-3)" />
          {reveal.expanded
            ? t.memoHideClosed
            : reveal.closedCount > 0
              ? t.memoRevealClosed.replace("{n}", String(reveal.closedCount))
              : t.memoRevealHistory}
        </button>
      ) : null}
    </div>
  );
}
